import {  useState } from 'react'
import { ShellSnippet } from '@/shared/components/ShellSnippet'
import type { OrgItem } from '@/lib/apiTypes'
import { s } from '@/features/integrations/guideStyles'
import type { GuideId } from '@/features/integrations/guides'
import { PlatformTabs, type Platform } from '@/features/integrations/components/PlatformTabs'
import {    TOKEN_EXCHANGE_CLI, TOKEN_EXCHANGE_GITHUB, TOKEN_EXCHANGE_GITLAB } from '@/features/integrations/snippets'

// ── Passive Drift Detection ─────────────────────────────────────────────────────
export function PassiveDriftGuide({ org, onSwitchGuide }: { org: OrgItem | null; token: string | null; onSwitchGuide: (id: GuideId) => void }) {
  const [platform, setPlatform] = useState<Platform>('cli')
  const orgSlug = org?.slug ?? '<org-slug>'
  const wsName  = '<workspace>'

  // Refresh-only plan + prior state serial/lineage. No comments here so these
  // strings stay safe to line-prefix into the CI snippets below.
  const planPrep = `terraform plan -refresh-only -out=drift.tfplan
PRIOR_SERIAL=$(terraform state pull | jq -r .serial)
PRIOR_LINEAGE=$(terraform state pull | jq -r .lineage)`

  // Push the redacted `show -json` render to the state workspace's plan key. Prior
  // serial/lineage ride along as headers so the app can compute staleness.
  const pushCurl = `terraform show -json drift.tfplan | curl -sf -X PUT \\
  "https://registry.terramantle.dev/plans/${orgSlug}/${wsName}.tfplan" \\
  -H "Authorization: Bearer $TOKEN" \\
  -H "X-Plan-Prior-Serial: $PRIOR_SERIAL" \\
  -H "X-Plan-Prior-Lineage: $PRIOR_LINEAGE" \\
  --data-binary @-`

  // Per-PR variant: X-Plan-Ref keeps concurrent plans from clobbering each other.
  const prCurl = `terraform show -json drift.tfplan | curl -sf -X PUT \\
  "https://registry.terramantle.dev/plans/${orgSlug}/${wsName}.tfplan" \\
  -H "Authorization: Bearer $TOKEN" \\
  -H "X-Plan-Ref: pr-\${PR_NUMBER}" \\
  -H "X-Plan-Prior-Serial: $PRIOR_SERIAL" \\
  -H "X-Plan-Prior-Lineage: $PRIOR_LINEAGE" \\
  --data-binary @-`

  const cliSnippet = `${TOKEN_EXCHANGE_CLI}

terraform init

# Refresh-only reconcile — runs with the cloud creds you already have.
# Nothing is applied, and Terramantle is handed no cloud access of its own.
${planPrep}

# Push the redacted JSON render — never the opaque binary .tfplan.
${pushCurl}`

  const githubSnippet = `name: passive-drift-monitor

on:
  schedule:
    - cron: '0 3 * * *'   # nightly at 03:00 UTC
  workflow_dispatch:       # allow manual runs

jobs:
  drift:
    runs-on: ubuntu-latest
    steps:
      - uses: actions/checkout@v4
      - uses: opentofu/setup-opentofu@v1

${TOKEN_EXCHANGE_GITHUB}

      - name: Passive drift scan
        env:
          TOKEN: \${{ steps.token.outputs.token }}
        run: |
          terraform init
${planPrep.split('\n').map((l) => '          ' + l).join('\n')}
${pushCurl.split('\n').map((l) => '          ' + l).join('\n')}`

  const gitlabSnippet = `image: hashicorp/terraform:latest

stages:
  - drift

passive-drift:
  stage: drift
  # Attach a nightly pipeline schedule in CI/CD → Schedules.
  rules:
    - if: '$CI_PIPELINE_SOURCE == "schedule"'
  script:
${TOKEN_EXCHANGE_GITLAB}
    - terraform init
${planPrep.split('\n').map((l) => '    - ' + l).join('\n')}
${pushCurl.split('\n').map((l) => '    - ' + l).join('\n')}`

  return (
    <div>
      <h3 style={s.guideTitle}>Passive Drift Detection</h3>
      <p style={s.para}>
        Detect infrastructure drift with <strong>zero cloud access</strong> for Terramantle. Your
        own <code style={s.code}>terraform plan -refresh-only</code> already reconciles state against
        the real world using the credentials you have — we simply ingest the redacted result. No
        cloud role, no read-only keys, nothing to hand over. Push the plan nightly and drift shows up
        on the <button style={s.linkBtn} onClick={() => onSwitchGuide('storing-state')}>State</button> workspace it belongs to.
      </p>

      <h3 style={s.subTitle}>Nightly passive drift monitor</h3>
      <p style={s.para}>
        Run a <code style={s.code}>-refresh-only</code> plan, capture the prior state serial and
        lineage (so we can flag drift computed against stale state), then PUT the{' '}
        <code style={s.code}>terraform show -json</code> render to the workspace's{' '}
        <code style={s.code}>.tfplan</code> key. We inherit the refresh you already ran — no
        Terramantle-owned cloud credentials are ever involved.
      </p>
      <p style={s.para}>Check out the <button style={s.linkBtn} onClick={() => onSwitchGuide('oidc-trust')}>Pipeline Trust</button> for GitHub &amp; GitLab seamless integrations.</p>
      <PlatformTabs value={platform} onChange={setPlatform} />
      {platform === 'cli'    && <ShellSnippet code={cliSnippet}    language="bash" />}
      {platform === 'github' && <ShellSnippet code={githubSnippet} language="yaml" />}
      {platform === 'gitlab' && <ShellSnippet code={gitlabSnippet} language="yaml" />}

      {platform === 'github' && (
        <p style={{ ...s.para, marginTop: 10 }}>
          Set <code style={s.code}>TM_CLIENT_ID</code> to the token's <strong>Client ID</strong> and <code style={s.code}>TM_CLIENT_SECRET</code> to its <strong>Client Secret</strong> in <strong>Settings → Secrets and variables → Actions</strong>. The <code style={s.code}>client_id</code> in the grant is the fixed <code style={s.code}>terramantle-bots</code>, not a secret.
        </p>
      )}
      {platform === 'gitlab' && (
        <p style={{ ...s.para, marginTop: 10 }}>
          Set <code style={s.code}>TM_CLIENT_ID</code> to the token's <strong>Client ID</strong> and <code style={s.code}>TM_CLIENT_SECRET</code> to its <strong>Client Secret</strong> in <strong>Org Settings ⚙ → CI/CD → Variables</strong> (masked). The <code style={s.code}>client_id</code> in the grant is the fixed <code style={s.code}>terramantle-bots</code>, not a secret.
        </p>
      )}

      <h3 style={s.subTitle}>Per-PR variant</h3>
      <p style={s.para}>
        Running drift on pull requests as well as the nightly job? Add an{' '}
        <code style={s.code}>X-Plan-Ref</code> header — a PR number, branch, or commit SHA — so
        concurrent plans are tracked side-by-side instead of clobbering each other. Everything else
        is identical:
      </p>
      <ShellSnippet code={prCurl} language="bash" />

      <h3 style={s.subTitle}>Notes</h3>
      <ul style={s.list}>
        <li>We push the <code style={s.code}>terraform show -json</code> render, <strong>not</strong> the opaque binary <code style={s.code}>drift.tfplan</code> — and values are redacted server-side before anything is stored.</li>
        <li>No cloud credentials reach Terramantle: the refresh runs in your pipeline with your existing provider auth; we only ingest the resulting plan.</li>
        <li><code style={s.code}>X-Plan-Prior-Serial</code> / <code style={s.code}>X-Plan-Prior-Lineage</code> let the app flag drift computed against a stale state serial.</li>
        <li>Use <code style={s.code}>X-Plan-Ref</code> (PR number, branch, or SHA) to keep concurrent plans separate; omit it and the latest push for the workspace wins.</li>
        <li>Pushing requires workspace <strong>write</strong> permission — the same bot token used for the state backend.</li>
      </ul>
    </div>
  )
}
